import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import CaseContextHeader from "../components/CaseContextHeader";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Upload, FlaskConical, ListChecks, Sparkles, CheckCircle2, AlertCircle, Wand2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function CaseDetail() {
  const { caseId } = useParams();
  const [data, setData] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [reextractOpen, setReextractOpen] = useState(false);
  const [reextractOpts, setReextractOpts] = useState({
    update_case_master: false,
    replace_parties: false,
    replace_properties: false,
    replace_lower_orders: false,
  });
  const [reextractRunning, setReextractRunning] = useState(false);

  const reload = () => {
    api.get(`/cases/${caseId}`).then((r) => setData(r.data));
    api.get(`/cases/${caseId}/checklist`).then((r) => setChecklist(r.data));
  };
  useEffect(reload, [caseId]);

  const startReextract = async () => {
    setReextractRunning(true);
    try {
      const { data: r } = await api.post(`/cases/${caseId}/reextract`, reextractOpts);
      toast.info("Re-extract running — polling for results");
      const start = Date.now();
      let status = "Running";
      let applied = null;
      while (status === "Running" && Date.now() - start < 180000) {
        await new Promise((res) => setTimeout(res, 3000));
        const { data: j } = await api.get(`/reextract/${r.reextract_job_id}`);
        status = j.status;
        applied = j.applied;
        if (j.error) throw new Error(j.error);
      }
      if (status === "Completed" && applied) {
        toast.success(
          `Re-extract complete · ${applied.parties} parties, ${applied.properties} properties, ${applied.lower_orders} lower-orders` +
          (applied.case_master_updated ? " · case master updated" : ""),
        );
        setReextractOpen(false);
        reload();
      } else {
        toast.error("Re-extract did not complete");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Re-extract failed");
    } finally {
      setReextractRunning(false);
    }
  };

  if (!data) return <Layout breadcrumbs={["SSRD", "Cases", "…"]}><div className="text-sm text-muted-foreground">Loading…</div></Layout>;
  const { case: c, parties, properties, lower_orders, documents } = data;

  return (
    <Layout breadcrumbs={[
      { label: "SSRD", to: "/dashboard" },
      { label: "Cases", to: "/cases" },
      `${c.case_number}/${c.case_year}`,
    ]}>
      <CaseContextHeader caseDoc={c} />

      <div className="flex flex-wrap gap-2 mb-6">
        <Link to={`/cases/${caseId}/documents`}><Button variant="outline" className="gap-2" data-testid="goto-documents-btn"><Upload className="size-4" /> Documents</Button></Link>
        <Link to={`/cases/${caseId}/processing`}><Button variant="outline" className="gap-2"><FlaskConical className="size-4" /> Processing</Button></Link>
        <Link to={`/cases/${caseId}/fields`}><Button variant="outline" className="gap-2"><ListChecks className="size-4" /> Field Validation</Button></Link>
        <Dialog open={reextractOpen} onOpenChange={setReextractOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="reextract-open-btn">
              <Wand2 className="size-4" /> Re-extract from Documents
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Re-extract case details</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground text-xs">
                Re-runs Remi8 extraction over every document attached to this
                case. By default new entries are <strong>appended</strong> — tick
                a box to <strong>replace</strong> existing entries first.
              </p>
              {[
                ["update_case_master", "Update case master (number, dates, district…)"],
                ["replace_parties", "Replace existing parties before adding new ones"],
                ["replace_properties", "Replace existing land/property entries"],
                ["replace_lower_orders", "Replace existing lower-authority orders"],
              ].map(([k, lbl]) => (
                <label key={k} className="flex items-start gap-2">
                  <Checkbox
                    checked={reextractOpts[k]}
                    onCheckedChange={(v) => setReextractOpts((s) => ({ ...s, [k]: !!v }))}
                    data-testid={`reextract-opt-${k}`}
                  />
                  <span className="text-xs">{lbl}</span>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReextractOpen(false)} disabled={reextractRunning}>Cancel</Button>
              <Button onClick={startReextract} disabled={reextractRunning} data-testid="reextract-start-btn">
                {reextractRunning ? "Running…" : "Run Re-extract"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Link to={`/cases/${caseId}/draft`}><Button className="gap-2" data-testid="goto-draft-btn"><Sparkles className="size-4" /> Generate Draft</Button></Link>
      </div>

      <Tabs defaultValue="parties">
        <TabsList className="grid grid-cols-5">
          <TabsTrigger value="parties">Parties ({parties.length})</TabsTrigger>
          <TabsTrigger value="land">Land ({properties.length})</TabsTrigger>
          <TabsTrigger value="lower">Lower Order ({lower_orders.length})</TabsTrigger>
          <TabsTrigger value="docs">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="checklist">Checklist</TabsTrigger>
        </TabsList>

        <TabsContent value="parties">
          <Card><CardContent className="p-0"><Table>
            <TableHeader><TableRow>
              <TableHead>Role</TableHead><TableHead>Name</TableHead>
              <TableHead>Address</TableHead><TableHead>Advocate</TableHead>
              <TableHead>Govt?</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {parties.map((p) => (
                <TableRow key={p.party_id}>
                  <TableCell>{p.party_role}</TableCell>
                  <TableCell className="font-medium">{p.party_name} <span className="text-muted-foreground font-normal">{p.father_or_spouse_name && `s/o ${p.father_or_spouse_name}`}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{[p.address_line, p.village, p.taluka, p.district].filter(Boolean).join(", ")}</TableCell>
                  <TableCell className="text-xs">{p.advocate_name || "—"}</TableCell>
                  <TableCell>{p.is_government_authority ? "Yes" : "No"}</TableCell>
                </TableRow>
              ))}
              {parties.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6 text-sm">No parties added.</TableCell></TableRow>}
            </TableBody>
          </Table></CardContent></Card>
        </TabsContent>

        <TabsContent value="land">
          <Card><CardContent className="p-0"><Table>
            <TableHeader><TableRow>
              <TableHead>Survey / Block</TableHead><TableHead>Village/Taluka/District</TableHead>
              <TableHead>Area</TableHead><TableHead>Use</TableHead><TableHead>Description</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {properties.map((p) => (
                <TableRow key={p.property_id}>
                  <TableCell className="font-mono text-xs">{p.survey_number} {p.block_number ? `/ ${p.block_number}` : ""}</TableCell>
                  <TableCell className="text-xs">{[p.village, p.taluka, p.district].filter(Boolean).join(", ")}</TableCell>
                  <TableCell className="text-xs">{p.land_area} {p.land_area_unit}</TableCell>
                  <TableCell className="text-xs">{p.land_use_type}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md">{p.disputed_property_description}</TableCell>
                </TableRow>
              ))}
              {properties.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6 text-sm">No properties.</TableCell></TableRow>}
            </TableBody>
          </Table></CardContent></Card>
        </TabsContent>

        <TabsContent value="lower">
          <Card><CardContent className="p-0"><Table>
            <TableHeader><TableRow>
              <TableHead>Authority</TableHead><TableHead>Order No.</TableHead><TableHead>Date</TableHead>
              <TableHead>Outcome</TableHead><TableHead>Summary</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {lower_orders.map((o) => (
                <TableRow key={o.lower_order_id}>
                  <TableCell>{o.authority_level} — {o.authority_name}</TableCell>
                  <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                  <TableCell className="text-xs">{o.order_date}</TableCell>
                  <TableCell><StatusBadge status={o.outcome_of_lower_order} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-lg">{o.challenged_order_summary}</TableCell>
                </TableRow>
              ))}
              {lower_orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6 text-sm">No lower orders.</TableCell></TableRow>}
            </TableBody>
          </Table></CardContent></Card>
        </TabsContent>

        <TabsContent value="docs">
          <Card><CardContent className="p-0"><Table>
            <TableHeader><TableRow>
              <TableHead>File</TableHead><TableHead>Category</TableHead><TableHead>Format</TableHead>
              <TableHead>OCR Req.</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {documents.map((d) => (
                <TableRow key={d.document_id}>
                  <TableCell className="text-xs">{d.file_name}</TableCell>
                  <TableCell className="text-xs">{d.document_category}</TableCell>
                  <TableCell className="text-xs">{d.document_format}</TableCell>
                  <TableCell className="text-xs">{d.ocr_required ? "Yes" : "No"}</TableCell>
                  <TableCell><StatusBadge status={d.processing_status || d.upload_status} /></TableCell>
                </TableRow>
              ))}
              {documents.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6 text-sm">No documents uploaded.</TableCell></TableRow>}
            </TableBody>
          </Table></CardContent></Card>
        </TabsContent>

        <TabsContent value="checklist">
          <Card><CardContent className="p-5">
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-3">Case Input Checklist</div>
            <ul className="space-y-2" data-testid="checklist-items">
              {(checklist?.items || []).map((i) => (
                <li key={i.key} className="flex items-center gap-3 text-sm">
                  {i.status
                    ? <CheckCircle2 className="size-4 text-emerald-700" />
                    : <AlertCircle className="size-4 text-red-700" />}
                  <span className={i.status ? "" : "text-red-700 font-medium"}>{i.label}</span>
                </li>
              ))}
            </ul>
            {checklist?.missing?.length > 0 && (
              <div className="mt-4 text-xs text-red-700 border border-red-200 bg-red-50 rounded-md p-3" data-testid="checklist-missing">
                Missing: {checklist.missing.join(" · ")}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
